import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { getServerData } from "../../util/get-server-data";
import { Category, useWebgate } from "@xvia/webgate-connect";
import { CategoryBanner, CategoryProps } from "./category-banner";
import { getCategories } from "../../apis/categories";
import { CategoryBannerContext } from "../../providers/category-banner-provider";
import { parseMunicipalityHomeFromSearch } from "../../util/municipality-context";

type CategoryBannerContainerProps = {
  municipioQuerySuffix?: string;
  municipalityName?: string;
  isMunicipalityHome?: boolean;
};

export const CategoryBannerContainer: FC<CategoryBannerContainerProps> = ({
  municipioQuerySuffix = "",
  municipalityName,
  isMunicipalityHome,
}) => {
  const { serverData } = useWebgate();
  let history = useHistory();
  const { codIbgeMunicipio: municipioCode } =
    parseMunicipalityHomeFromSearch(municipioQuerySuffix);

  const { isOpen, onClickHeader } = useContext(CategoryBannerContext);
  const [categoryList, setCategoryList] = useState<Category[]>(
    serverData?.categoryList || getServerData("categoryList", null)
  );
  const fetchedKeyRef = useRef<string>("");
  const getCategory = useCallback(() => {
    getCategories(municipioCode)
      .then((result) => {
        setCategoryList(result);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [municipioCode]);

  const onClickCategory = useCallback(
    (category: Category) => {
      history.push(`/list/${category.slug}${municipioQuerySuffix}`);
    },
    [history, municipioQuerySuffix]
  );

  const handleHeaderClick = useCallback(() => {
    onClickHeader();
  }, [onClickHeader]);

  useEffect(() => {
    // Em contexto municipal, sempre recarregamos para garantir que o backend
    // retorne somente categorias vinculadas ao município selecionado.
    if (municipioCode != null) {
      const fetchKey = `municipio-${municipioCode}`;
      if (fetchedKeyRef.current === fetchKey) return;

      fetchedKeyRef.current = fetchKey;
      getCategory();
      return;
    }

    // Em contexto estadual, evitamos refetch desnecessário quando já há dados.
    if (categoryList?.length) return;

    const fetchKey = "default";
    if (fetchedKeyRef.current === fetchKey) return;

    fetchedKeyRef.current = fetchKey;
    getCategory();
  }, [categoryList?.length, getCategory, municipioCode]);

  const props: CategoryProps = {
    categoryList,
    onClickCategory,
    onClickHeader: handleHeaderClick,
    onClickMunicipalityCitiesBanner: () => history.push("/list/municipios"),
    isOpen,
    headerSubtitle: isMunicipalityHome
      ? `Clique para acessar ${municipalityName || "a cidade"}`
      : "Clique para expandir",
    municipalityCitiesSubtitle: "Clique para acessar",
    showMunicipalityCitiesBanner: !isMunicipalityHome,
    hideChevron: !!isMunicipalityHome,
  };

  return <CategoryBanner {...props} />;
};
